import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import { Concept, ConceptRef, DimensionalConceptRef, Deprecation, Label } from './ConceptSlice';
import { TaxonomyTypeEnum, TaxonomyVersionInfo } from './TaxonomyVersionSlice';
import { DimensionReferenceInput, FactInput } from './AfrSlice';
import { CustomReferenceRole, Reference, Part, PartDefinition, getUasId, getPart, PartValue } from './TaxonomyReferenceSlice';
import http from '../utils/http';
import utilityHelper from '../utils/utilityHelper';
import enums from '../utils/enums';


export interface ReferenceAssignmentChange {
    reference: Reference;
    concept: Concept;
    remove: boolean;
}
export interface ReferenceAssignmentInput {
    referenceId: string;
    conceptId: string;
    remove: boolean;
}
export interface AccountScopeInput {

    accountId: string;
    axisId: string;
    accountScope: string[];
}

export interface CustomReferenceRoleInput {
    name: string;
    documentation: string;
}

export interface PartDefinitionInput {
    name: string;
    documentation: string;
    datatype: string;    
}

export interface PartInput {
    partName: string;
    partValue: string;
}
export interface ReferenceInput {
    referenceType: string;
    parts: PartInput[];
}
export interface RelationshipInput {
    networkId: string;
    role: string | null;
    fromId: string;
    toId: string;
    remove: boolean | null;
}
export interface AccountScope {
    accountId: string;
    scopeIds: string[];
}
export interface DimensionalConceptRefInput {
    conceptId: string;
    dimensions: DimensionReferenceInput[];
}

export interface AccountDependencyInput {
    conceptId: string | null;
    dependency: DimensionalConceptRefInput[] | null;
    recursiveDependencies: AccountDependencyInput[] | null;
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    name: string | null;
    dimensionQualifiers: DimensionReferenceInput[] | null;
    dimensionDisQualifiers: DimensionReferenceInput[] | null;
    dimensionsToMatch: DimensionReferenceInput[] | null;
    decimals: number | null;
}
export interface AccountRootDependencyInput {
    ruleId: string | null;
    conceptId: string | null;
    dependency: DimensionalConceptRefInput[] | null;
    recursiveDependencies: AccountDependencyInput[] | null;
    preconditions: AccountDependencyInput[];
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    name: string | null;
    dimensionQualifiers: DimensionReferenceInput[] | null;
    dimensionDisQualifiers: DimensionReferenceInput[] | null;
    dimensionsToMatch: DimensionReferenceInput[] | null;
    newName: string | null;
    remove: boolean | null;
    message: string | null;
    deprecationDate: string | null;
    decimals: number | null;
}
export interface AccountDependencySetInput {
    accountId: string;
    dependencies: AccountRootDependencyInput[];
}
export interface EditDeprecation {
    conceptId: string;
    deprecationDate: string | undefined;
    reason: string;
    replacementType: string; // one of simple, aggregateToPart, partToAggregate
    replacements: DimensionalConceptRef[];
    new: boolean | null;
    dirty: boolean | null;
}

export interface ConceptLabelInput {
    conceptId: string;
    role: string;
    value: string | null;
}

export interface DeprecationInput {
    conceptId: string;
    deprecationDate: string;
    reason: string;
    replacementType: string; // one of simple, aggregateToPart, partToAggregate
    replacements: DimensionalConceptRefInput[];

}
export interface ConceptInput {
    id: string;
    isAbstract: boolean | null;
    isCredit: boolean | null;
    datatype: string;
    periodtype: string;
    labels: Array<ConceptLabelInput> | null;
}

export interface MemberConceptInput {
    id: string;
    labels: Array<ConceptLabelInput> | null;
}

export interface TaxonomyUpdate {
    year: number;
    references: Array<ReferenceInput>;
    referenceAssignments: Array<ReferenceAssignmentInput>;
    partDefinitions: Array<PartDefinitionInput>;
    labels: Array<ConceptLabelInput>;
    customRoles: Array<CustomReferenceRoleInput>;
    deprecations: Array<DeprecationInput>;
    relationships: Array<RelationshipInput>;
    concepts: Array<ConceptInput>;
    accountScopes: Array<AccountScopeInput>;
    accountDependencies: Array<AccountDependencySetInput>;
}

export interface ConceptLabelChange {
    conceptId: string;
    changedLabels: any;
    droppedLabels: string[];
}
export interface TaxonomyChangesState {
    referenceChanges: Array<Reference>;
    referenceAssignmentChanges: Array<ReferenceAssignmentChange>;
    conceptLabelChanges: Array<ConceptLabelInput>;
    conceptChanges: ConceptInput[];
    partDefinitionChanges: PartDefinition[];
    customRoleChanges: CustomReferenceRole[];
    taxonomyVersion: TaxonomyVersionInfo;
    editReference: Reference | null;
    deprecations: EditDeprecation[];
    relationshipChanges: RelationshipInput[];
    entityScopeChanges: any;
    dimensionalScopeChanges: any;
    accountDependencyChanges: any;
    saveReports: TaxonomyUpdateReport[];
    status: 'idle' | 'loading' | 'failed';
    patchesApplied: string[];
}
export interface TaxonomyUpdateReport_deprecated {
    numberOfChanges: number;
}
export interface TaxonomyUpdateReport {
    success: boolean;
    report: string;
}
export interface CommitTaxonomyChangesRequest {
    taxonomyYear: number;
}

const initialState: TaxonomyChangesState = {
    referenceChanges: [],
    referenceAssignmentChanges: [],
    conceptLabelChanges: [],
    conceptChanges: [],
    partDefinitionChanges: [],
    customRoleChanges: [],
    taxonomyVersion: {
        year: 0,
        type: TaxonomyTypeEnum.AFR,
        periods: null,
        isPublished: true
    },
    editReference: null,
    deprecations: [],
    relationshipChanges: [],
    entityScopeChanges: {},
    dimensionalScopeChanges: {},
    accountDependencyChanges: {},
    saveReports: [],
    status: "idle",
    patchesApplied: []
}

export interface TaxonomyChangesCommitCall {
    taxonomyYear: number;
    submit: boolean;
}
export const getLabelValue = function (ci: ConceptInput, lblRole : string, def : string ) : string {
    if (ci.labels == null) {
        return def;
    }
    for (var lbl of ci.labels) {
        var lrole = lbl.role.toLowerCase();
        if (lrole.endsWith(lblRole)) {
            if (lbl.value) {
                return lbl.value;
            }
        }
    }
    return def;
}
const createCommit = function (state: TaxonomyChangesState): TaxonomyUpdate {
    var commitReferences: ReferenceInput[] = [];
    for (var reference of state.referenceChanges) {
        var partsInput : any[] = [];
        for (var p of reference.parts) {
            var pi: PartInput = {
                partName: p.definition.name,
                partValue: p.value
            }
            partsInput.push(pi);
        }
        var refInput: ReferenceInput = {
            referenceType: reference.type,
            parts: partsInput
        }
        commitReferences.push(refInput);
    }
    var commitReferenceAssignments: ReferenceAssignmentInput[] = [];
    for (var rac of state.referenceAssignmentChanges) {
        var rai: ReferenceAssignmentInput = {
            referenceId: getUasId(rac.reference,null),
            conceptId: rac.concept.id,
            remove: rac.remove
        }
        commitReferenceAssignments.push(rai)
    }
    var commitPartDefinitions: PartDefinitionInput[] = [];
    for (var pd of state.partDefinitionChanges) {
        var pdName = pd.name;
        if (pd.group && pd.group.length > 0) {
            pdName = pd.group + "/" + pd.name;
        }
        var pdi: PartDefinitionInput = {
            name: pdName,
            documentation: pd.documentation,
            datatype: pd.datatype
        }
        commitPartDefinitions.push(pdi);
    }
    var commitCustomRoles: CustomReferenceRoleInput[] = [];
    for (var cr of state.customRoleChanges) {
        var cri: CustomReferenceRoleInput = {
            name: cr.name,
            documentation: cr.documentation
        }
        commitCustomRoles.push(cri);
    }
    var commitDeprecations: DeprecationInput[] = [];
    for (var dep of state.deprecations) {
        var reps: DimensionalConceptRefInput[] = [];
        if (dep.replacements) {
            for (var rep of dep.replacements) {
                var dims: DimensionReferenceInput[] = [];
                if (rep.dimensions) {
                    for (var dim of rep.dimensions) {
                        if (dim.member != null) {
                            var dimInput: DimensionReferenceInput = {
                                axisId: dim.axis.id,
                                memberId: dim.member.id,
                                typedContent: dim.typedContent
                            };
                            dims.push(dimInput);
                        }
                    }
                }
                var repInput: DimensionalConceptRefInput = {
                    conceptId: rep.conceptId,
                    dimensions: dims
                }
                reps.push(repInput);
            }
        }
        var depInput: DeprecationInput = {
            conceptId: dep.conceptId,
            deprecationDate: dep.deprecationDate != null && dep.deprecationDate != undefined ? dep.deprecationDate : "",
            reason: dep.reason,
            replacementType: dep.replacementType,
            replacements: reps
        }
        commitDeprecations.push(depInput);
    }
    var scopeInputs: AccountScopeInput[] = [];
    // First process the entity scope changes, then process the dimensional scope changes
    // NOTE: In most cases these there will be only one or the other used by a concept - but they're not mutually exclusive and we could end up with two records for
    // the same account being submitted. However the axis#member notation of the dimensional reference distinguished those entries.
    for (var scopeKey of Object.keys(state.entityScopeChanges)) {
        var scopeInput: AccountScopeInput = {
            accountId: scopeKey,
            axisId: "ReportingUnitAxis",
            accountScope: state.entityScopeChanges[scopeKey]
        }
        scopeInputs.push(scopeInput);
    }
    for (var scopeKey of Object.keys(state.dimensionalScopeChanges)) {
        var scopeInput: AccountScopeInput = {
            accountId: scopeKey,
            axisId: "*",
            accountScope: state.dimensionalScopeChanges[scopeKey]
        }
        scopeInputs.push(scopeInput);
    }
    var acctDeps : any[] = [];
    for (var depKey of Object.keys(state.accountDependencyChanges)) {
        acctDeps.push(state.accountDependencyChanges[depKey]);
    }

    var commit: TaxonomyUpdate = {
        year: state.taxonomyVersion.year,
        references: commitReferences,
        referenceAssignments: commitReferenceAssignments,
        partDefinitions: commitPartDefinitions,
        labels: state.conceptLabelChanges,
        customRoles: commitCustomRoles,
        deprecations: commitDeprecations,
        relationships: state.relationshipChanges,
        concepts: state.conceptChanges,
        accountScopes: scopeInputs,
        accountDependencies: acctDeps
    }
    return commit;
}
export const commitChanges = createAsyncThunk(
    "Taxonomy/CommitChanges",
    async (state : TaxonomyChangesState): Promise<TaxonomyUpdateReport | null> => {
        try {
            var commitRequest: TaxonomyUpdate = createCommit(state);
            const result = await http<TaxonomyUpdate, TaxonomyUpdateReport>({
                path: `/taxonomy/commitChanges`,
                method: 'post',
                body: commitRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    } 
);

export const immediateUpdate = createAsyncThunk(
    "Taxonomy/ImmediateUpdate",
    async (commitRequest: TaxonomyUpdate): Promise<TaxonomyUpdateReport | null> => {
        try {
            const result = await http<TaxonomyUpdate, TaxonomyUpdateReport>({
                path: `/taxonomy/commitChanges`,
                method: 'post',
                body: commitRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    }
);

export const applyPatches = createAsyncThunk(
    "Taxonomy/ApplyPatches",
    async (patchList: string): Promise<string[]> => {
        try {
            const result = await http<undefined, string[]>({
                path: `/taxonomy/applyPatches`
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return [];
        }
    }
);

// Check to see if there is a locally modified version of this reference
export const getLatestReferenceVersion = function (reference: Reference, tState: TaxonomyChangesState): Reference {
    var result: Reference = reference;
    var changeSet: Array<Reference> = tState.referenceChanges;
    if (changeSet.length > 0) {
        var uasid = getUasId(reference,null);
        var changedVersion = changeSet.find(cref => cref.type === reference.type && getUasId(cref, null) === uasid)
        if (changedVersion) {
            return changedVersion;
        }
    }
    return reference;
};

const applyConceptLabelChange = function (c: Concept, chg: ConceptLabelInput) {
    if (chg.value == null) {
        return;
    }
    switch (chg.role) {
        case "label":
            c.name = chg.value;
            break;

        case "documentation":
            c.documentation = chg.value;
            break;

        default:
            var newLabels: Label[] = [];
            c.labels = newLabels;
            break;
    }
}
export const getLatestConceptVersion = function (c: Concept | null, tState: TaxonomyChangesState): Concept | null {
    if (!c) {
        return null;
    }
    var result: Concept = c;
    var changeSet: Array<ConceptLabelInput> = tState.conceptLabelChanges;
    if (changeSet.length > 0) {
        var changes = changeSet.filter(cref => cref.conceptId === c.id)
        if (changes && changes.length > 0) {
            // make a copy
            result = { ...c };
            changes.map(chg => applyConceptLabelChange(result,chg));
        }
    }
    return result;
};

export const haveChangesForReference = function (reference: Reference, tState: TaxonomyChangesState): boolean {
    var changeSet: Array<Reference> = tState.referenceChanges;
    if (changeSet.length > 0) {
        var uasid = getUasId(reference, null);
        var changedVersion = changeSet.find(cref => cref.type === reference.type && getUasId(cref, null) === uasid)
        if (changedVersion) {
            return true;
        }
    }
    return false;
};

export const includeChangedReferencesForConcept = function (concept: Concept, tState: TaxonomyChangesState): Array<Reference> {
    var changeSet: Array<Reference> = tState.referenceChanges;
    var changeAssignSet: Array<ReferenceAssignmentChange> = tState.referenceAssignmentChanges;

    if (changeSet.length === 0 && changeAssignSet.length === 0) {
        return concept.references;
    }
    var conceptId = concept.id;
    var assignmentMatches: Array<ReferenceAssignmentChange> = changeAssignSet.filter(rac => rac.concept.id === conceptId);
    if (assignmentMatches.length === 0) {
        return concept.references;
    }

    var assignmentMap : any = {};
    for (var cRA of assignmentMatches) {
        // Deliberately overwrite here so we end up with the last state of each assignment change
        var rid = getUasId(cRA.reference, null);
        assignmentMap[rid] = cRA;
    }

    var result : any[] = [];
    for (var existing of concept.references) {
        var ridExisting = getUasId(existing, null);
        if (assignmentMap.hasOwnProperty(ridExisting)) {
            var ra : ReferenceAssignmentChange = assignmentMap[ridExisting];
            if (!ra.remove) {
                result.push(ra.reference);
            }
            delete assignmentMap[ridExisting];
        } else {
            result.push(existing);
        }
    }
    // Now add any left over assignments
    for (var key in assignmentMap) {
        var raLeftOver: ReferenceAssignmentChange = assignmentMap[key];
        if (!raLeftOver.remove) {
            result.push(raLeftOver.reference);
        }
    }
    return result;
};

export const anyChanges = function (state: TaxonomyChangesState | null) {
    if (!state) {
        return false;
    }
    if( state.referenceChanges.length > 0 ) {
        return true;
    }
    if( state.referenceAssignmentChanges.length > 0 ) {
        return true;
    }
    if( state.conceptLabelChanges.length > 0 ) {
        return true;
    }
    if( state.conceptChanges.length > 0 ) {
        return true;
    }
    if (state.customRoleChanges.length > 0) {
        return true;
    }
    if (state.partDefinitionChanges.length > 0) {
        return true;
    }
    if (state.deprecations.length > 0) {
        return true;
    }
    if (state.relationshipChanges.length > 0) {
        return true;
    }
    if (Object.keys(state.entityScopeChanges).length > 0) {
        return true;
    }
    if (Object.keys(state.dimensionalScopeChanges).length > 0) {
        return true;
    }
    if (Object.keys(state.accountDependencyChanges).length > 0) {
        return true;
    }
    return false;
}
export const getDeprecationFor = function (deps: EditDeprecation[], conceptId: string): EditDeprecation | null {
    var matches: EditDeprecation[] = deps.filter(dep => dep.conceptId === conceptId);
    if (matches.length === 0) {
        return null;
    }
    return matches[matches.length - 1];
}

export const getEditableDeprecation = function (concept: Concept): EditDeprecation | null {
    var cdep: Deprecation | null = concept.deprecation;
    if (cdep == null) {
        return null;
    }
    return {
        conceptId: concept.code,
        deprecationDate: cdep.deprecationDate,
        reason: cdep.reason,
        replacements: cdep.replacements,
        replacementType: "",
        new: false,
        dirty: false
    }
}
export const getChangeConceptId = function(cchg: ConceptInput) : string {
    if (cchg.id) {
        return cchg.id;
    }
    return "no id";
}

export const getChangeConceptLabel = function (cchg: ConceptInput, rname : string ): string {
    if (cchg.labels) {
        var lbl: ConceptLabelInput | undefined = cchg.labels.find(lbl => lbl.role.toLowerCase().endsWith( rname ) );
        if (lbl == null || !lbl.value) {
            return "no " + rname;
        }
        return lbl.value;
    }
    return "no " + rname;
}
const getRuleName = function (dep: AccountRootDependencyInput): string {
    if (dep.name) {
        return dep.name;
    }
    return "unknown";
}
const removeChangesFromMap = function (changes: any, changeIdMap: any, prefix: string): any {
    var retainedChanges: any = {};
    Object.keys(changes).filter((key, index) => !changeIdMap.hasOwnProperty(prefix + key)).map(key => retainedChanges[key] = changes[key]);
    return retainedChanges;
}

export const taxonomyChangesSlice = createSlice({
    name: 'TaxonomyChanges',
    initialState,
    reducers: {
        addReferenceChange: (state: any, action: any) => {
            var r: Reference = { ...action.payload };
            r.dirty = false; // set this to false - the dirty flag is only used for changes that have not been saved to this slice.
            state.referenceChanges.push(r);
        },
        addReferenceRoleChange: (state: any, action: any) => {
            var r: CustomReferenceRole = { ...action.payload };
            state.customRoleChanges.push(r);
        },
        addPartDefinitionChange: (state: any, action: any) => {
            var pd: PartDefinition = { ...action.payload };
            state.partDefinitionChanges.push(pd);
        },
        addReferenceAssignmentChange: (state: any, action: any) => {
            state.referenceAssignmentChanges.push(action.payload);
        },
        addDeprecation: (state: any, action: any) => {
            state.deprecations.push(action.payload);
        },
        addConceptLabelChange: (state: any, action: any) => {
            state.conceptLabelChanges.push(action.payload);
        },
        addConceptChange: (state: any, action: any) => {
            state.conceptChanges.push(action.payload);
        },
        addMemberConceptChange: (state: any, action: any) => {
            var memConcept: MemberConceptInput = action.payload;
            var c: ConceptInput = {
                id: memConcept.id,
                isAbstract: null,
                isCredit: null,
                datatype: "DTR_TYPES_DOMAINITEMTYPE",
                periodtype: "duration",
                labels: memConcept.labels
            }
            state.conceptChanges.push(c);
        },
        addRelationshipChange: (state: any, action: any) => {
            state.relationshipChanges.push(action.payload);
        },
        addEntityScopeChange: (state: any, action: any) => {
            const ascope: AccountScope = action.payload;
            state.entityScopeChanges[ascope.accountId] = ascope.scopeIds;
        },
        addDimensionalScopeChange: (state: any, action: any) => {
            const ascope: AccountScope = action.payload;
            state.dimensionalScopeChanges[ascope.accountId] = ascope.scopeIds;
        },
        addAccountDependencyChange: (state: any, action: any) => {
            const aDepSet: AccountDependencySetInput = action.payload;
            if (state.accountDependencyChanges.hasOwnProperty(aDepSet.accountId)) {
                // We must merge the new changes with what is already present
                var priorDepSet : AccountDependencySetInput = state.accountDependencyChanges[aDepSet.accountId];
                var priorDeps: any = {};
                priorDepSet.dependencies.map(dep => priorDeps[getRuleName(dep)] = dep);
                aDepSet.dependencies.map(newDep => priorDeps[getRuleName(newDep)] = newDep)
                var newDepList: AccountRootDependencyInput[] = [];
                Object.keys(priorDeps).map(k => newDepList.push({ ...priorDeps[k] }));
                priorDepSet.dependencies = newDepList;
            } else {
                // This is a new addition to the change set
                state.accountDependencyChanges[aDepSet.accountId] = aDepSet;
            }
        },
        clearEditReference: (state: any) => {
            state.editReference = null;
        },
        setEditReference: (state: any, action: any) => {
            var r: Reference = { ...action.payload };
            r.dirty = true; // set this to true - the dirty flag is only used for changes that have not been saved to this slice.
            state.editReference = r;
        },
        setTaxonomyChangeVersion: (state: any, action: any) => {
            var newVersionId = action.payload;
           if (newVersionId.type !== state.taxonomyVersion.type || newVersionId.year !== state.taxonomyVersion.year) {
               state.taxonomyVersion = { ...newVersionId };
                state.referenceChanges = [];
                state.referenceAssignmentChanges = [];
                state.conceptLabelChanges = [];
                state.conceptChanges = [];
                state.partDefinitionChanges = [];
                state.customRoleChanges = [];
                state.editReference = null;
                state.deprecations = [];
                state.relationshipChanges = [];
                state.entityScopeChanges = { };
                state.dimensionalScopeChanges = { };
                state.accountDependencyChanges = { };
                state.saveReports = [];
                state.status = "idle";
                state.patchesApplied = [];

            }
        },
        clearChanges: (state: any) => {
            return initialState;
        },
        updateEditReferencePart: {
            reducer(state: any, action: any) {
                var pv: PartValue = action.payload;
                if (state.editReference) {
                    if (pv.partValue.length === 0) {
                        // we remove the part
                        var newParts : any[] = [];
                        for (var nextp of state.editReference.parts) {
                            if (nextp.definition.name !== pv.partName) {
                                newParts.push({ ...nextp });
                            }
                        }
                        state.editReference.parts = newParts;
                    } else {
                        var p: Part | null = getPart(state.editReference, pv.partName);
                        if (p) {
                            p.value = pv.partValue;
                        }
                    }
                    state.editReference.dirty = true;
                }
            },
            prepare(payload: PartValue) {
                return {
                    payload
                }
            }
        },
        removeSelectedChanges: {
            reducer(state: TaxonomyChangesState, action: any) {
                var changeIdList: string[] = action.payload;
                var changeIdMap : any = {};
                changeIdList.map(cid => changeIdMap[cid] = cid);
                state.accountDependencyChanges = removeChangesFromMap(state.accountDependencyChanges, changeIdMap, enums.changeTrackingPrefix.DEPENDENCYCHANGE);
                state.entityScopeChanges = removeChangesFromMap(state.entityScopeChanges, changeIdMap, enums.changeTrackingPrefix.ENTITYSCOPECHANGE);
                state.dimensionalScopeChanges = removeChangesFromMap(state.dimensionalScopeChanges, changeIdMap, enums.changeTrackingPrefix.DIMENSIONALSCOPECHANGE);
                state.conceptChanges = state.conceptChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.CONCEPTCHANGE + index));
                state.conceptLabelChanges = state.conceptLabelChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.LABELCHANGE + index));
                state.customRoleChanges = state.customRoleChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.CUSTOMROLECHANGE + index));
                state.deprecations = state.deprecations.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.DEPRECATIONCHANGE + index));
                state.partDefinitionChanges = state.partDefinitionChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.PARTDEFINITIONCHANGE + index));
                state.referenceAssignmentChanges = state.referenceAssignmentChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.REFASSIGNCHANGE + index));
                state.referenceChanges = state.referenceChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.REFCHANGE + index));
                state.relationshipChanges = state.relationshipChanges.filter((chg, index) => !changeIdMap.hasOwnProperty(enums.changeTrackingPrefix.RELATIONSHIPCHANGE + index));
            },
            prepare(payload: string[]) {
                return {
                    payload
                }
            }
        },

    },
    extraReducers: (builder: any) => {
        builder
            .addCase(commitChanges.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(commitChanges.fulfilled, (state: TaxonomyChangesState, action: any) => {
                var rep: TaxonomyUpdateReport | null = action.payload;
                if (rep) {
                    state.saveReports.push(rep);
                }
                state.referenceChanges = [];
                state.referenceAssignmentChanges = [];
                state.conceptLabelChanges = [];
                state.conceptChanges = [];
                state.deprecations = [];
                state.conceptLabelChanges = [];
                state.relationshipChanges = [];
                state.editReference = null;
                state.entityScopeChanges = {};
                state.dimensionalScopeChanges = {};
                state.accountDependencyChanges = {};
                state.status = "idle";
            })
            .addCase(immediateUpdate.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(immediateUpdate.fulfilled, (state: TaxonomyChangesState, action: any) => {
                // Don't do any local update
                state.status = "idle";
            })
            .addCase(applyPatches.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(applyPatches.fulfilled, (state: any, action: any) => {
                var results: string[] = action.payload;
                if (results) {
                    state.patchesApplied = results;
                }
                state.status = "idle";
            })
    },
});

export const { addRelationshipChange, addMemberConceptChange, addConceptChange, addConceptLabelChange, addDeprecation, addReferenceChange, addReferenceRoleChange, addPartDefinitionChange, addReferenceAssignmentChange, clearEditReference, setEditReference, updateEditReferencePart, clearChanges, setTaxonomyChangeVersion, addEntityScopeChange, addDimensionalScopeChange, addAccountDependencyChange, removeSelectedChanges } = taxonomyChangesSlice.actions;

export const getTaxonomyChanges = (state: RootState) => state.taxonomyChanges ? state.taxonomyChanges : null;

export const getConceptChanges = (state: RootState) => (state.taxonomyChanges && state.taxonomyChanges.conceptChanges) ? state.taxonomyChanges.conceptChanges : [];

export const getRelationshipChanges = (state: RootState) => (state.taxonomyChanges && state.taxonomyChanges.relationshipChanges) ? state.taxonomyChanges.relationshipChanges : [];

export const getPartDefinitionChanges = (state: RootState) => (state.taxonomyChanges && state.taxonomyChanges.partDefinitionChanges) ? state.taxonomyChanges.partDefinitionChanges : [];

export const getReferenceRoleChanges = (state: RootState) => (state.taxonomyChanges && state.taxonomyChanges.customRoleChanges) ? state.taxonomyChanges.customRoleChanges : [];

export const selectEditReference = (state: RootState) => state.taxonomyChanges ? state.taxonomyChanges.editReference : null;

export const getDeprecations = (state: RootState) => state.taxonomyChanges ? state.taxonomyChanges.deprecations : [];

export const getAccountDependencyChanges = (state: RootState) => state.taxonomyChanges ? state.taxonomyChanges.accountDependencyChanges : {};

export default taxonomyChangesSlice.reducer;